import styles from './choose-your-maintenance.module.css'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import { useMaintenanceContext } from '../../context/maintenance-context'
import {
  Checkbox,
  Text,
  Alert,
  InlineRadio,
  SvgIcon,
  TextLink,
} from '@cinch-labs/design-system'
import { useRef, useState } from 'react'
import { serviceOptionDefaults, VehicleType } from '../../types'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useServiceOptionsExplainedDrawer } from './service-options-explained'
import { datadogRum } from '@datadog/browser-rum'

export const ChooseYourMaintenance: React.FC = () => {
  const {
    vehicleMake,
    vehicleModel,
    vehicleFuelType,
    vehicleVin,
    vrm,
    store,
    handleNext,
    dispatch,
    step,
    serviceOptions,
  } = useMaintenanceContext()

  const [mot, setMot] = useState<boolean>(false)
  const [fuelType, setFuelType] = useState<VehicleType>(VehicleType.COMBUSTIBLE)
  const [oilAndFilter, setOilAndFilter] = useState<boolean>(false)
  const [standardService, setStandardService] = useState<boolean>(false)
  const [majorService, setMajorService] = useState<boolean>(false)
  const [error, setError] = useState<string>()
  const isCheckboxesSet = useRef(false)
  const { trackEvent } = useAnalytics()

  const { setShowServiceOptionsExplainedDrawer, setIsEv } =
    useServiceOptionsExplainedDrawer()

  const title = 'Choose your service'
  const description = `For your ${vehicleMake} ${vehicleModel} (${vrm})${
    store ? ` at ${store}` : ''
  }`

  const onContinue = () => {
    if (mot || oilAndFilter || standardService || majorService) {
      const isEV = fuelType === VehicleType.EV
      const serviceOptions = {
        ...serviceOptionDefaults,
        ...(mot
          ? { mot: { ...serviceOptionDefaults.mot, selected: true } }
          : {}),
        ...(oilAndFilter && !isEV
          ? {
              oilAndFilter: {
                ...serviceOptionDefaults.oilAndFilter,
                selected: true,
              },
            }
          : {}),
        ...(standardService
          ? isEV
            ? {
                standardServiceEV: {
                  ...serviceOptionDefaults.standardServiceEV,
                  selected: true,
                },
              }
            : {
                standardService: {
                  ...serviceOptionDefaults.standardService,
                  selected: true,
                },
              }
          : {}),
        ...(majorService
          ? isEV
            ? {
                majorServiceEV: {
                  ...serviceOptionDefaults.majorServiceEV,
                  selected: true,
                },
              }
            : {
                majorService: {
                  ...serviceOptionDefaults.majorService,
                  selected: true,
                },
              }
          : {}),
      }

      const serviceOptionsSelectedNames = Object.values(serviceOptions)
        .reduce((agg, cur) => (cur.selected ? agg + cur.name + ', ' : agg), '')
        .replace(/, $/, '')

      dispatch({
        type: 'SET_SELECTED_SERVICE_OPTIONS',
        serviceOptions,
        serviceOptionsSelectedNames,
        serviceOptionsSelectedTotalPrice: Object.values(serviceOptions ?? {})
          .reduce((agg, cur) => (cur.selected ? agg + cur.price : agg), 0)
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      })

      const chooseMaintenance = {
        eventName: `Select date time`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'service_type_selected',
            label: 'make_a_booking',
            type: serviceOptionsSelectedNames,
          },
        },
      }

      trackEvent(chooseMaintenance)

      datadogRum.addAction('Cinch Servicing: Service Selected', {
        step,
        page: window.location.pathname,
        serviceOptionsSelectedNames,
      })

      handleNext()
    } else {
      setError('Please select at least one option')
    }
  }

  if (!isCheckboxesSet.current) {
    setOilAndFilter(serviceOptions?.oilAndFilter.selected ?? false)
    setMot(serviceOptions?.mot.selected ?? false)
    setStandardService(serviceOptions?.standardService.selected ?? false)
    setMajorService(serviceOptions?.majorService.selected ?? false)
    isCheckboxesSet.current = true
  }

  const serviceOptionsExplainedButtonText = 'Service options explained'
  const showFuelTypeSection = false // future feature

  return (
    <CarMaintenanceStepWrapper
      title={title}
      description={description}
      onContinue={onContinue}
    >
      {showFuelTypeSection && (
        <div className={styles.fuelTypeContainer}>
          <Text
            fontSizeFixed="s"
            fontWeight="semibold"
            className={styles.fuelTypeHeading}
          >
            Whats's your cars fuel type?
          </Text>
          <Text fontSizeFixed="xs" className={styles.fuelTypeText}>
            We’ll suggest the right service options based on your car’s fuel
            type.
          </Text>
          <div className={styles.fuelTypeInlineRadioContainer}>
            <InlineRadio
              htmlFor="Petrol/Diesel/Hybrid"
              name="fuelType"
              value={VehicleType.COMBUSTIBLE}
              checked={fuelType === VehicleType.COMBUSTIBLE}
              onChange={() => {
                setFuelType(VehicleType.COMBUSTIBLE)
                setIsEv(false)
                setError('')
                dispatch({
                  type: 'SET_VEHICLE',
                  make: vehicleMake ?? '',
                  model: vehicleModel ?? '',
                  vrm: vrm ?? '',
                  vin: vehicleVin ?? '',
                  fuelType: vehicleFuelType ?? '',
                })
              }}
            >
              Petrol/Diesel/Hybrid
            </InlineRadio>
            <InlineRadio
              htmlFor="Electric"
              name="fuelType"
              value={VehicleType.EV}
              checked={fuelType === VehicleType.EV}
              onChange={() => {
                setFuelType(VehicleType.EV)
                setIsEv(true)
                setError('')
                dispatch({
                  type: 'SET_VEHICLE',
                  make: vehicleMake ?? '',
                  model: vehicleModel ?? '',
                  vrm: vrm ?? '',
                  vin: vehicleVin ?? '',
                  fuelType: vehicleFuelType ?? '',
                })
              }}
            >
              Electric
            </InlineRadio>
          </div>
        </div>
      )}
      {showFuelTypeSection && <hr className={styles.hr} />}

      <div className={styles.optionsContainer}>
        <Text element="h2" fontSizeFixed="m" fontWeight="bold">
          Choose at least one option
        </Text>
        <Checkbox
          label={`${serviceOptionDefaults.mot.name} - £${serviceOptionDefaults.mot.price}`}
          id="mot"
          name="mot"
          checked={mot}
          onChange={() => {
            setMot((mot) => !mot)
            setError('')
          }}
          error={{ show: false, message: 'This needs to be checked' }}
          description="(Every 12 Months)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
            icon: 'icon',
          }}
          disabled={false}
        />
        {fuelType === VehicleType.COMBUSTIBLE && (
          <Checkbox
            label={`${serviceOptionDefaults.oilAndFilter.name} - £${serviceOptionDefaults.oilAndFilter.price}`}
            id="oil"
            name="oil"
            checked={oilAndFilter}
            onChange={() => {
              setOilAndFilter((oil) => !oil)
              setError('')
            }}
            description="(Every 5-7,000 miles)"
            classNames={{
              wrapper: styles.checkbox,
            }}
            disabled={false}
          />
        )}
        <Checkbox
          label={`${
            fuelType === VehicleType.EV
              ? serviceOptionDefaults.standardServiceEV.name
              : serviceOptionDefaults.standardService.name
          } - £${
            fuelType === VehicleType.EV
              ? serviceOptionDefaults.standardServiceEV.price
              : serviceOptionDefaults.standardService.price
          }`}
          id="standard"
          name="standard"
          checked={standardService}
          onChange={() => {
            setStandardService((standardService) => !standardService)
            setError('')
          }}
          description="(Every 10-12,000 miles)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
            icon: 'icon',
          }}
          disabled={false}
        />
        <Checkbox
          label={`${
            fuelType === VehicleType.EV
              ? serviceOptionDefaults.majorServiceEV.name
              : serviceOptionDefaults.majorService.name
          } - £${
            fuelType === VehicleType.EV
              ? serviceOptionDefaults.majorServiceEV.price
              : serviceOptionDefaults.majorService.price
          }`}
          id="major"
          name="major"
          checked={majorService}
          onChange={() => {
            setMajorService((majorService) => !majorService)
            setError('')
          }}
          description="(Every 20-24,000 miles)"
          classNames={{
            wrapper: styles.checkbox,
            label: 'label',
          }}
          disabled={false}
        />
      </div>
      <div className={styles.serviceOptionsLink}>
        <div className={styles.infoIcon}>
          <SvgIcon name="info" color="toreaBay" height={16} />
        </div>
        <TextLink
          element={'a'}
          textProps={{ fontSizeFixed: 's', fontWeight: 'semibold' }}
          onClick={() => {
            const serviceOptionsExplainedEvent = {
              eventName: `Service options explained drawer opened`,
              type: TrackingEventTypes.ADOBE,
              data: {
                event: {
                  name: EventNames.click,
                  category: 'cinch_servicing',
                  action: 'open',
                  label: 'service-options-explained',
                  type: 'drawer',
                },
              },
            }
            trackEvent(serviceOptionsExplainedEvent)
            setShowServiceOptionsExplainedDrawer(true)
          }}
        >
          {serviceOptionsExplainedButtonText}
        </TextLink>
      </div>
      {error && <Alert>{error}</Alert>}
    </CarMaintenanceStepWrapper>
  )
}
